import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import classNames from "classnames";

import {
  GetSchoolListResponse,
  SchoolListTableItem,
  capitalizeFirstLetter,
} from "@ascendnow/shared";

import AscScrollableTable, {
  AscScrollableTableProps,
} from "../AscScrollableTable/AscScrollableTable";
import AscButton from "../AscButton/AscButton";
import AscSearchInput from "../AscInputFields/AscSearchInput/AscSearchInput";
import AscLink from "../AscLink/AscLink";
import AscAddNewSchoolModal from "./AscAddNewSchoolModal/AscAddNewSchoolModal";
import { useAscContext } from "../../context";
import { formatErrorMessage } from "../../lib";
import AscEditSchoolModal from "./AscEditSchoolModal/AscEditSchoolModal";
import styles from "./AscSchoolListTable.module.scss";

export interface AscSchoolListTableProps {
  className?: string;
  onGetSchoolList: (
    sort?:
      | "newest"
      | "oldest"
      | "nameAsc"
      | "nameDesc"
      | "studentsAsc"
      | "studentsDesc"
      | "creditsAsc"
      | "creditsDesc"
      | string,
    name?: string,
    take?: number,
    skip?: number
  ) => Promise<GetSchoolListResponse>;
  getSchoolRoute?: (schoolId: string) => string;
  onAddNewSchool?: (value: {
    schoolName: string;
    adminEmail: string;
    credits: number;
  }) => Promise<void>;
  onEditSchool?: (
    schoolId: string,
    values: {
      schoolName: string;
      email: string;
      addCredits?: number;
      deleteCredits?: number;
    }
  ) => Promise<void>;
  resendEmail: (email: string) => Promise<void>;
}

const columns: AscScrollableTableProps<keyof SchoolListTableItem>["columns"] = [
  {
    name: "School Name",
    selector: "name",
    sortable: true,
  },
  {
    name: "Credits",
    selector: "credits",
    sortable: true,
  },
  {
    name: "Admin E-mail",
    selector: "email",
  },
  {
    name: "Active Students",
    selector: "students",
    sortable: true,
  },

  {
    name: "Edit",
    selector: "id",
  },
  {
    name: "View",
    selector: "action",
  },
  {
    name: "Resend Email",
    selector: "sendEmail",
  },
];

const DEFAULT_PER_PAGE = 20;

export default function AscSchoolListTable({
  className,
  onGetSchoolList,
  getSchoolRoute,
  onAddNewSchool,
  onEditSchool,
  resendEmail,
}: AscSchoolListTableProps) {
  const router = useRouter();
  const sortBy = router.query.sortBy as string;
  const sortOrder = router.query.sortOrder as "asc" | "desc";
  const page = Number(router.query.page) || 1;
  const [schools, setSchools] = useState<SchoolListTableItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const { toast } = useAscContext();
  const [showAddNewSchoolModal, setShowAddNewSchoolModal] =
    useState<boolean>(false);
  const [showEditSchoolModal, setShowEditSchoolModal] =
    useState<boolean>(false);
  const [schoolToEdit, setSchoolToEdit] = useState<SchoolListTableItem>();

  const schoolTableData = useMemo(
    () =>
      schools?.map((school) => ({
        name: school?.name,
        credits: school?.credits,
        email: school?.email,
        students: school?.students,
        id: (
          <AscButton
            size="small"
            onClick={() => {
              setShowEditSchoolModal(true);
              setSchoolToEdit(school);
            }}
          >
            Edit
          </AscButton>
        ),
        action: (
          <AscLink href={getSchoolRoute?.(school?.name)}>
            <AscButton size="small">View</AscButton>
          </AscLink>
        ),
        sendEmail: (
          <AscButton size="small" onClick={() => resendEmail(school?.email)}>
            Send
          </AscButton>
        ),
      })),
    [schools]
  );

  async function getSchools(filters?: {
    sort?: { field: string; direction: "asc" | "desc" };
    page?: number;
    searchTerm?: string;
  }) {
    try {
      setLoading(true);
      const direction = capitalizeFirstLetter(filters?.sort?.direction || "");
      const res = await onGetSchoolList(
        `${filters?.sort?.field ? filters?.sort?.field + direction : ""}`,
        searchTerm,
        DEFAULT_PER_PAGE,
        (page - 1) * DEFAULT_PER_PAGE
      );
      setSchools(
        res.schools?.map((school) => ({
          id: school?.id,
          name: school?.name,
          credits: school?.credits,
          email: school?.email,
          students: school?.studentCount,
          sendEmail: true,
        }))
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddNewSchool(values: {
    schoolName: string;
    adminEmail: string;
    credits: number;
  }) {
    try {
      await onAddNewSchool?.(values);
      setShowAddNewSchoolModal(false);
      getSchools({
        sort: { field: sortBy, direction: sortOrder },
        page: page,
        searchTerm,
      });
      toast?.success("Successfully added new school");
    } catch (error) {
      toast?.error(formatErrorMessage(error, "Failed to add new school"));
    }
  }

  async function handleEditSchool(values: {
    schoolName: string;
    email: string;
    addCredits?: number;
    deleteCredits?: number;
  }) {
    const schoolId = schoolToEdit?.id;
    if (!schoolId) {
      return;
    }
    try {
      await onEditSchool?.(schoolId, values);
      setShowEditSchoolModal(false);
      getSchools({
        sort: { field: sortBy, direction: sortOrder },
        page: page,
        searchTerm,
      });
      toast?.success("Successfully edited school");
    } catch (error) {
      toast?.error(formatErrorMessage(error, "Failed to edit school"));
    }
  }

  function handleSearch() {
    getSchools({
      sort: { field: sortBy, direction: sortOrder },
      page: page,
      searchTerm,
    });
  }

  useEffect(() => {
    getSchools({
      sort: { field: sortBy, direction: sortOrder },
      page: page,
      searchTerm,
    });
  }, [sortBy, sortOrder, page]);

  useEffect(() => {
    if (searchTerm.length < 1) {
      getSchools({
        sort: { field: sortBy, direction: sortOrder },
        page: 1,
      });
    }
  }, [searchTerm]);

  return (
    <div
      data-testid="asc-school-list-table"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.topBar}>
        <div className={styles.searchField}>
          <AscSearchInput
            value={searchTerm}
            placeholder="Search by name..."
            onChange={(event) => setSearchTerm(event.target.value)}
            className={styles.input}
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <AscButton
            onClick={handleSearch}
            disabled={loading}
            className={styles.button}
          >
            Search
          </AscButton>
        </div>
        <AscButton
          className={styles.addButton}
          onClick={() => setShowAddNewSchoolModal(true)}
        >
          Add School
        </AscButton>
      </div>

      <AscScrollableTable
        title="School list"
        loading={loading}
        columns={columns}
        data={schoolTableData}
        pageCount={schoolTableData?.length >= DEFAULT_PER_PAGE ? page + 1 : 1}
      />
      {showAddNewSchoolModal ? (
        <AscAddNewSchoolModal
          visible={showAddNewSchoolModal}
          onClose={() => setShowAddNewSchoolModal(false)}
          onSubmit={handleAddNewSchool}
        />
      ) : null}
      {schoolToEdit && showEditSchoolModal ? (
        <AscEditSchoolModal
          visible={showEditSchoolModal}
          onClose={() => setShowEditSchoolModal(false)}
          initialValues={{
            schoolName: schoolToEdit.name,
            email: schoolToEdit.email,
          }}
          onSubmit={handleEditSchool}
        />
      ) : null}
    </div>
  );
}
